import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./customizeSpotTheme.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { apiUrl } from "../../../utils/apiUrl";

const ITEM_TEXT_MAX_LEN = 60;
const CATEGORY_NAME_MAX_LEN = 50;
const MAX_CATEGORY_COUNT = 3;
const MIN_QUESTIONS_PER_CATEGORY = 1;
const MAX_QUESTIONS_PER_CATEGORY = 10;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const REQUIRED_CATEGORY_IMAGE_W = 800;
const REQUIRED_CATEGORY_IMAGE_H = 800;

function validateQuestionCount(count, categoryLabel) {
  const name = categoryLabel || "This category";
  if (count < MIN_QUESTIONS_PER_CATEGORY) {
    return `${name} must have at least ${MIN_QUESTIONS_PER_CATEGORY} question.`;
  }
  if (count > MAX_QUESTIONS_PER_CATEGORY) {
    return `${name} can have at most ${MAX_QUESTIONS_PER_CATEGORY} questions.`;
  }
  return null;
}

function validateAllCategories(cats) {
  for (const category of cats) {
    const err = validateQuestionCount(category.items?.length ?? 0, category.displayName);
    if (err) return err;
  }
  return null;
}

function publicDownloadUrl(path) {
  // Use S3-hosted sample CSVs (production-safe; avoids SPA rewrites / API gateway routing).
  const base = "https://2023-extramileplay-php.s3.ap-south-1.amazonaws.com/UAT/cubicle_chaos";
  const p = String(path || "").replace(/^\/+/, "");
  return `${base}/${p.replace(/^sample-csv\//, "")}`;
}
const SAMPLE_CSV_FILES = [
  { label: "Sample CSV (one round)", file: "sample_single_category_questions.csv" },
  { label: "Sample CSV (all rounds)", file: "sample_all_categories_questions.csv" },
];

function toSlugKey(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}
function formatKey(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeCsvHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function parseCsvLine(line) {
  const cells = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      cells.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells.map((cell) => String(cell || "").trim());
}

function parseCsvObjects(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 2) return [];

  const headers = parseCsvLine(lines[0]).map(normalizeCsvHeader);
  return lines.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    return headers.reduce((acc, header, index) => {
      acc[header] = String(cells[index] || "").trim();
      return acc;
    }, {});
  });
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => resolve(String(ev.target?.result || ""));
    reader.onerror = () => reject(new Error("Failed to read CSV file."));
    reader.readAsText(file);
  });
}

/** Renders an item image with a proper fallback.
 *  Resets error state whenever `src` changes (e.g. after a new upload),
 *  so stale inline-style display:none never survives a src update. */
function ItemImage({ src, alt, className }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [src]);

  if (!src || failed) {
    return (
      <div className={`catmgr-item-no-img ${className || ""}`}>
        <i className="fa-solid fa-image" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={`catmgr-item-img ${className || ""}`}
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
    />
  );
}

const CustomizeSpotTheme = () => {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((s) => s.theme);

  // categories: [{ key, displayName, image, items:[{image,name}] }]
  const [categories, setCategories]   = useState([]);
  const [activeCategoryKey, setActiveCategoryKey] = useState("");
  const [, setSaving]           = useState(false);
  const [csvLoading, setCsvLoading]   = useState(false);

  // ── Category modals ─────────────────────────────────────────
  const [addCatOpen,   setAddCatOpen]   = useState(false);
  const [newCatName,   setNewCatName]   = useState("");
  const [editCatModal, setEditCatModal] = useState(null); // { index }
  const [editCatName,  setEditCatName]  = useState("");
  const [catImageFile, setCatImageFile] = useState(null);
  const [catImagePreview, setCatImagePreview] = useState("");
  const [catImageFilename, setCatImageFilename] = useState("");
  const [catUploading, setCatUploading] = useState(false);
  const catFileRef = useRef(null);
  const singleCsvInputRef = useRef(null);
  const bulkCsvInputRef = useRef(null);

  // ── Item modal (shared add/edit) ────────────────────────────
  // mode: 'add' | 'edit'
  const [itemModal,    setItemModal]    = useState(null); // { mode, catIndex, itemIndex? }
  const [itemName,     setItemName]     = useState("");

  // ── Load data ────────────────────────────────────────────────
  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    // Avoid wiping local list when API returns a partial theme object without this key.
    if (!("category_images_names" in data)) return;
    const cin  = data.category_images_names || {};
    const meta = ("category_meta" in data) ? (data.category_meta || {}) : {};
    const cats = ("categories" in data) ? (data.categories || []) : [];
    const result = [];
    cats.forEach((displayName) => {
      const key = toSlugKey(displayName);
      const metaEntry = meta[key] || {};
      result.push({
        key,
        displayName: metaEntry.label || displayName,
        image: metaEntry.image || "",
        items: [...(cin[key] || [])],
      });
    });
    Object.keys(meta).forEach((key) => {
      if (!result.find((r) => r.key === key)) {
        const metaEntry = meta[key] || {};
        result.push({
          key,
          displayName: metaEntry.label || formatKey(key),
          image: metaEntry.image || "",
          items: [...(cin[key] || [])],
        });
      }
    });
    Object.keys(cin).forEach((key) => {
      if (!result.find((r) => r.key === key))
        result.push({ key, displayName: formatKey(key), image: "", items: [...(cin[key] || [])] });
    });
    setCategories(result);
  }, [data]);

  useEffect(() => {
    if (!categories.length) {
      setActiveCategoryKey("");
      return;
    }
    if (!categories.some((c) => c.key === activeCategoryKey)) {
      setActiveCategoryKey(categories[0].key);
    }
  }, [categories, activeCategoryKey]);

  // ── Save all ─────────────────────────────────────────────────
  const handleSave = async (cats) => {
    if (!currentTheme) { Swal.fire("No theme", "Select a theme first.", "error"); return; }
    const countErr = validateAllCategories(cats);
    if (countErr) {
      Swal.fire("Invalid questions", countErr, "warning");
      return;
    }
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            categories: cats.map((c) => c.displayName),
            category_images_names: Object.fromEntries(cats.map((c) => [c.key, c.items])),
            category_meta: Object.fromEntries(
              cats.map((c) => [c.key, { label: c.displayName, image: c.image || "" }])
            ),
          },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Saved", "Categories updated.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed.", "error");
    }
    setSaving(false);
  };

  // ── Category CRUD ────────────────────────────────────────────
  const resetCategoryModalState = () => {
    if (catImagePreview) URL.revokeObjectURL(catImagePreview);
    setCatImageFile(null);
    setCatImagePreview("");
    setCatImageFilename("");
    setCatUploading(false);
    if (catFileRef.current) catFileRef.current.value = "";
  };

  const closeAddCategoryModal = () => {
    setAddCatOpen(false);
    setNewCatName("");
    resetCategoryModalState();
  };

  const closeEditCategoryModal = () => {
    setEditCatModal(null);
    setEditCatName("");
    resetCategoryModalState();
  };

  const handleCategoryFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpeg", "jpg", "png"].includes(ext)) {
      Swal.fire("Invalid File", "Only JPEG / JPG / PNG allowed.", "error");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      Swal.fire("File too large", "Maximum 5 MB.", "error");
      e.target.value = "";
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      if (img.width !== REQUIRED_CATEGORY_IMAGE_W || img.height !== REQUIRED_CATEGORY_IMAGE_H) {
        URL.revokeObjectURL(url);
        setCatImageFile(null);
        setCatImagePreview("");
        Swal.fire(
          "Invalid image size",
          `Image must be exactly ${REQUIRED_CATEGORY_IMAGE_W}×${REQUIRED_CATEGORY_IMAGE_H} pixels. Your image is ${img.width}×${img.height}.`,
          "error"
        );
        e.target.value = "";
        return;
      }

      if (catImagePreview) URL.revokeObjectURL(catImagePreview);
      setCatImageFile(file);
      setCatImagePreview(url);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      Swal.fire("Invalid image", "Could not read image dimensions.", "error");
      e.target.value = "";
    };

    img.src = url;
  };

  const handleAddCategory = async () => {
    const name = newCatName.trim();
    if (!name) { Swal.fire("Required", "Category name is required.", "warning"); return; }
    if (categories.length >= MAX_CATEGORY_COUNT) {
      Swal.fire(
        "Limit Reached",
        `You can add up to ${MAX_CATEGORY_COUNT} categories only.`,
        "warning"
      );
      return;
    }
    const key = toSlugKey(name);
    if (categories.find((c) => c.key === key)) {
      Swal.fire("Duplicate", "A category with this name already exists.", "warning"); return;
    }
    let imageFilename = catImageFilename;
    if (catImageFile) {
      setCatUploading(true);
      try { imageFilename = await uploadThemeImage(catImageFile); }
      catch (e) { Swal.fire("Upload Error", e.message, "error"); setCatUploading(false); return; }
      setCatUploading(false);
    }
    const updatedCats = [...categories, { key, displayName: name, image: imageFilename || "", items: [] }];
    setCategories(updatedCats);
    setActiveCategoryKey(key);
    closeAddCategoryModal();
    await handleSave(updatedCats);
  };

  const handleOpenAddCategory = () => {
    if (categories.length >= MAX_CATEGORY_COUNT) {
      Swal.fire("Limit Reached", "You’ve reached the maximum number of categories.", "warning");
      return;
    }
    setNewCatName("");
    resetCategoryModalState();
    setAddCatOpen(true);
  };

  const handleEditCategory = async () => {
    const name = editCatName.trim();
    if (!name) { Swal.fire("Required", "Category name is required.", "warning"); return; }
    const current = categories[editCatModal.index];
    if (!current) return;
    const nextKey = toSlugKey(name);
    if (!nextKey) {
      Swal.fire("Required", "Category name is required.", "warning");
      return;
    }
    if (categories.some((c, i) => i !== editCatModal.index && c.key === nextKey)) {
      Swal.fire("Duplicate", "A category with this name already exists.", "warning");
      return;
    }
    let imageFilename = catImageFilename || current.image || "";
    if (catImageFile) {
      setCatUploading(true);
      try { imageFilename = await uploadThemeImage(catImageFile); }
      catch (e) { Swal.fire("Upload Error", e.message, "error"); setCatUploading(false); return; }
      setCatUploading(false);
    }
    const updatedCats = categories.map((c, i) =>
      i === editCatModal.index ? { ...c, key: nextKey, displayName: name, image: imageFilename } : c
    );
    setCategories(updatedCats);
    if (activeCategoryKey === current.key) setActiveCategoryKey(nextKey);
    closeEditCategoryModal();
    await handleSave(updatedCats);
  };

  const handleDeleteCategory = (index) => {
    Swal.fire({
      title: "Delete Category?",
      text: `"${categories[index].displayName}" and all its items will be removed.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Yes, delete",
    }).then(async (r) => {
      if (r.isConfirmed) {
        const updatedCats = categories.filter((_, i) => i !== index);
        setCategories(updatedCats);
        if (activeCategoryKey === categories[index]?.key) {
          setActiveCategoryKey(updatedCats[0]?.key || "");
        }
        await handleSave(updatedCats);
      }
    });
  };

  // ── Upload helper ─────────────────────────────────────────────
  const uploadThemeImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("themeName", String(currentTheme));
    const res  = await fetch(apiUrl("/uploadCategoryImage"), {
      method: "POST",
      headers: { Authorization: `Bearer ${adminToken}` },
      body: fd,
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Upload failed");
    return json.filename;
  };

  const closeItemModal = () => {
    setItemModal(null);
    setItemName("");
  };

  const handleItemNameChange = (val) => {
    let next = val;
    if (next.length > ITEM_TEXT_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Question can be at most ${ITEM_TEXT_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, ITEM_TEXT_MAX_LEN);
    }
    setItemName(next);
  };

  const openAddItemModal = (catIndex) => {
    setItemModal({ mode: "add", catIndex });
    setItemName("");
  };

  // ── Item CRUD ─────────────────────────────────────────────────
  const handleSaveItem = async () => {
    const trimmed = itemName.trim();
    if (!trimmed) {
      Swal.fire("Required", "Question is required.", "warning");
      return;
    }
    if (trimmed.length > ITEM_TEXT_MAX_LEN) {
      Swal.fire(
        "Invalid value",
        `Question must be at most ${ITEM_TEXT_MAX_LEN} characters.`,
        "warning"
      );
      return;
    }
    const name = trimmed;

    const { mode, catIndex, itemIndex } = itemModal;
    const targetCategory = categories[catIndex];

    if (mode === "add") {
      const addErr = validateQuestionCount(
        (targetCategory?.items?.length ?? 0) + 1,
        targetCategory?.displayName
      );
      if (addErr) {
        Swal.fire("Limit reached", addErr, "warning");
        return;
      }
    }

    let updatedCats;
    if (mode === "add") {
      updatedCats = categories.map((c, i) =>
        i === catIndex
          ? { ...c, items: [...c.items, { image: "", name, contentType: "text" }] }
          : c
      );
    } else {
      updatedCats = categories.map((c, ci) =>
        ci === catIndex
          ? {
              ...c,
              items: c.items.map((it, ii) =>
                ii === itemIndex ? { ...it, image: "", name, contentType: "text" } : it
              ),
            }
          : c
      );
    }

    setCategories(updatedCats);
    closeItemModal();
    await handleSave(updatedCats);
  };

  const handleDeleteItem = (catIndex, itemIndex) => {
    const category = categories[catIndex];
    if ((category?.items?.length ?? 0) <= MIN_QUESTIONS_PER_CATEGORY) {
      Swal.fire(
        "Cannot delete",
        `${category?.displayName || "This category"} must keep at least ${MIN_QUESTIONS_PER_CATEGORY} question.`,
        "warning"
      );
      return;
    }

    Swal.fire({
      title: "Delete Item?",
      text: `"${categories[catIndex].items[itemIndex].name}" will be removed.`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Yes, delete",
    }).then(async (r) => {
      if (r.isConfirmed) {
        const updatedCats = categories.map((c, ci) =>
          ci === catIndex ? { ...c, items: c.items.filter((_, ii) => ii !== itemIndex) } : c
        );
        const countErr = validateQuestionCount(
          updatedCats[catIndex]?.items?.length ?? 0,
          updatedCats[catIndex]?.displayName
        );
        if (countErr) {
          Swal.fire("Cannot delete", countErr, "warning");
          return;
        }
        setCategories(updatedCats);
        await handleSave(updatedCats);
      }
    });
  };

  const openEditItemModal = (catIndex, itemIndex) => {
    const item = categories[catIndex]?.items?.[itemIndex];
    if (!item) return;
    setItemModal({ mode: "edit", catIndex, itemIndex });
    setItemName(String(item.name ?? "").slice(0, ITEM_TEXT_MAX_LEN));
  };

  const getQuestionFromCsvRow = (row) => String(
    row.question || row.name || row.text || row.prompt || ""
  ).trim();

  const csvRowHasCategoryValue = (row) =>
    Boolean(String(row.category || row.round || "").trim());

  const assertSingleRoundCsvFormat = (rows) => {
    const headers = Object.keys(rows[0] || {});
    const hasCategoryHeader = headers.some((h) => h === "category" || h === "round");
    const hasCategoryData = rows.some(csvRowHasCategoryValue);
    if (hasCategoryHeader && hasCategoryData) {
      throw new Error(
        'This CSV uses the "all rounds" format (category column). Use "Upload All Categories CSV" instead, or download "Sample CSV (one round)" which has only a "question" column.'
      );
    }
  };

  const assertBulkRoundCsvFormat = (rows) => {
    const headers = Object.keys(rows[0] || {});
    const hasCategoryHeader = headers.some((h) => h === "category" || h === "round");
    if (!hasCategoryHeader) {
      throw new Error(
        'Bulk upload requires "category" and "question" columns. For one round only, use "Upload Selected CSV" and "Sample CSV (one round)".'
      );
    }
    if (!rows.some(csvRowHasCategoryValue)) {
      throw new Error(
        'No category values found. Use "Sample CSV (all rounds)" with a "category" column (e.g. Round 1, Round 2).'
      );
    }
  };

  const handleSingleCategoryCsvUpload = async (file) => {
    const activeCategory = categories.find((c) => c.key === activeCategoryKey);
    if (!activeCategory) {
      throw new Error("Select a category first.");
    }

    const text = await readFileAsText(file);
    const rows = parseCsvObjects(text);
    if (!rows.length) {
      throw new Error("CSV file is empty or missing data rows.");
    }

    assertSingleRoundCsvFormat(rows);

    const items = [];
    for (let i = 0; i < rows.length; i += 1) {
      const question = getQuestionFromCsvRow(rows[i]);
      if (!question) continue;
      if (question.length > ITEM_TEXT_MAX_LEN) {
        throw new Error(
          `Row ${i + 2}: Question must be at most ${ITEM_TEXT_MAX_LEN} characters.`
        );
      }
      items.push({ name: question, image: "", contentType: "text" });
    }
    if (!items.length) {
      throw new Error('CSV must contain a "question" column with at least one question.');
    }
    const countErr = validateQuestionCount(items.length, activeCategory.displayName);
    if (countErr) {
      throw new Error(
        `${countErr} Your CSV has ${items.length} valid question${items.length === 1 ? "" : "s"}.`
      );
    }

    const confirm = await Swal.fire({
      title: "Replace questions?",
      text: `This will replace all questions in ${activeCategory.displayName}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, replace",
    });
    if (!confirm.isConfirmed) return;

    const updatedCats = categories.map((category) =>
      category.key === activeCategory.key
        ? { ...category, items }
        : category
    );

    setCategories(updatedCats);
    await handleSave(updatedCats);
    await Swal.fire("Uploaded", `${items.length} questions imported successfully.`, "success");
  };

  const handleBulkCsvUpload = async (file) => {
    const text = await readFileAsText(file);
    const rows = parseCsvObjects(text);
    if (!rows.length) {
      throw new Error("CSV file is empty or missing data rows.");
    }

    assertBulkRoundCsvFormat(rows);

    const grouped = new Map();
    const missingCategories = new Set();

    rows.forEach((row, rowIndex) => {
      const categoryName = String(row.category || row.round || "").trim();
      if (!categoryName) return;

      const key = toSlugKey(categoryName);
      const existing = categories.find((category) => category.key === key);
      if (!existing) {
        missingCategories.add(categoryName);
        return;
      }

      if (!grouped.has(existing.key)) {
        grouped.set(existing.key, {
          displayName: existing.displayName,
          items: [],
        });
      }

      const question = getQuestionFromCsvRow(row);
      if (!question) return;
      if (question.length > ITEM_TEXT_MAX_LEN) {
        throw new Error(
          `Row ${rowIndex + 2}: Question must be at most ${ITEM_TEXT_MAX_LEN} characters.`
        );
      }
      grouped.get(existing.key).items.push({ name: question, image: "", contentType: "text" });
    });

    if (missingCategories.size > 0) {
      throw new Error(
        `CSV contains unknown categories: ${Array.from(missingCategories).join(", ")}`
      );
    }

    if (grouped.size === 0) {
      throw new Error('CSV must contain "category" and "question" columns with valid rows.');
    }

    for (const [, group] of grouped) {
      const countErr = validateQuestionCount(group.items.length, group.displayName);
      if (countErr) {
        throw new Error(
          `${countErr} "${group.displayName}" has ${group.items.length} valid question${
            group.items.length === 1 ? "" : "s"
          } in the CSV.`
        );
      }
    }

    const confirm = await Swal.fire({
      title: "Replace questions for matching categories?",
      text: "This will replace questions in the categories listed in the CSV file.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, replace",
    });
    if (!confirm.isConfirmed) return;

    const updatedCats = categories.map((category) => {
      const group = grouped.get(category.key);
      if (!group) return category;
      return {
        ...category,
        items: group.items,
      };
    });

    setCategories(updatedCats);
    await handleSave(updatedCats);
    await Swal.fire(
      "Uploaded",
      `Imported questions for ${grouped.size} categor${grouped.size === 1 ? "y" : "ies"}.`,
      "success"
    );
  };

  const handleCsvFileChange = async (e, mode) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "csv") {
      Swal.fire("Error", "Use CSV file only.", "error");
      return;
    }

    setCsvLoading(true);
    try {
      if (mode === "single") {
        await handleSingleCategoryCsvUpload(file);
      } else {
        await handleBulkCsvUpload(file);
      }
    } catch (error) {
      if (error?.message) {
        Swal.fire("Error", error.message, "error");
      }
    } finally {
      setCsvLoading(false);
    }
  };

  // ── Filter ────────────────────────────────────────────────────
  const S3        = process.env.REACT_APP_S3_PATH || "";
  const gameName  = data?.themeName || data?.themename || "Cubicle Chaos";
  const totalItems = categories.reduce((s, c) => s + c.items.length, 0);
  const activeCategory = categories.find((c) => c.key === activeCategoryKey) || null;
  const activeCategoryIndex = activeCategory
    ? categories.findIndex((c) => c.key === activeCategory.key)
    : -1;
  const visibleItems = activeCategory ? activeCategory.items : [];

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="catmgr-page">

        {/* ── Page Header ── */}
        <div className="catmgr-header">
          <div className="catmgr-header-left">
            <h2 className="catmgr-title">
              <i className="fa-solid fa-layer-group" /> &nbsp;{gameName} — Category Manager
            </h2>
            <p className="catmgr-subtitle">
              {categories.length} categories &nbsp;·&nbsp; {totalItems} total questions
              <br />
              <span className="catmgr-pair-hint">
                Manage each round in table format. Each category must have{" "}
                {MIN_QUESTIONS_PER_CATEGORY}–{MAX_QUESTIONS_PER_CATEGORY} questions (CSV included).
                Questions are text-only.
              </span>
            </p>
          </div>
          <div className="catmgr-header-right">
            <button
              className="catmgr-btn catmgr-btn-primary"
              onClick={() => navigate("/admin/live")}
            >
              <i className="fa-solid fa-broadcast-tower" /> Live
            </button>
          </div>
        </div>

        {/* ── Two-column body ── */}
        <div className="catmgr-body">

          {/* ══ LEFT: Categories list ══════════════════════════════ */}
          <div className="catmgr-left-panel">
            <div className="catmgr-panel-card">
              <div className="catmgr-panel-head">
                <span className="catmgr-panel-title">
                  <i className="fa-solid fa-list" /> Categories
                </span>
                <button
                  className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                  onClick={handleOpenAddCategory}
                  title={
                    categories.length >= MAX_CATEGORY_COUNT
                      ? `Maximum ${MAX_CATEGORY_COUNT} categories allowed`
                      : "Add Category"
                  }
                >
                  <i className="fa-solid fa-plus" /> Add
                </button>
              </div>

              {categories.length === 0 && (
                <p className="catmgr-no-items">No categories yet.</p>
              )}

              <ul className="catmgr-cat-list">
                {categories.map((cat, i) => (
                  <li
                    key={cat.key}
                    className={`catmgr-cat-row${activeCategoryKey === cat.key ? " is-active" : ""}`}
                    onClick={() => setActiveCategoryKey(cat.key)}
                  >
                    <div className="catmgr-cat-info">
                      <div className="catmgr-cat-preview">
                        <ItemImage
                          src={cat.image ? S3 + cat.image : ""}
                          alt={cat.displayName}
                          className="catmgr-cat-thumb"
                        />
                        <div className="catmgr-cat-meta">
                          <span className="catmgr-cat-display">{cat.displayName}</span>
                          <span className="catmgr-cat-key">{cat.key}</span>
                        </div>
                      </div>
                    </div>
                    <div className="catmgr-cat-actions">
                      <button
                        className="catmgr-icon-btn catmgr-icon-btn-sm"
                        title="Edit"
                        onClick={() => {
                          setEditCatModal({ index: i });
                          setEditCatName(cat.displayName);
                          resetCategoryModalState();
                          setCatImageFilename(cat.image || "");
                        }}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                        title="Delete"
                        onClick={() => handleDeleteCategory(i)}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ══ RIGHT: Category items ══════════════════════════════ */}
          <div className="catmgr-right-panel">
            {!activeCategory && <div className="catmgr-empty">No categories found.</div>}

            {activeCategory && (
              <div className="catmgr-card">
                <div className="catmgr-card-header catmgr-card-header-stacked">
                  <div className="catmgr-tabs">
                    {categories.map((cat) => (
                      <button
                        key={cat.key}
                        type="button"
                        className={`catmgr-tab-btn${activeCategoryKey === cat.key ? " is-active" : ""}`}
                        onClick={() => setActiveCategoryKey(cat.key)}
                      >
                        {cat.displayName}
                      </button>
                    ))}
                  </div>

                  <div className="catmgr-card-toolbar">
                    <div className="catmgr-card-title-block">
                      <h3 className="catmgr-card-name">{activeCategory.displayName}</h3>
                      <span className="catmgr-card-key">{activeCategory.key}</span>
                      <span className="catmgr-badge">
                        {activeCategory.items.length} / {MAX_QUESTIONS_PER_CATEGORY} questions
                      </span>
                    </div>

                    <div className="catmgr-table-actions">
                      <button
                        className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                        onClick={() => {
                          if (activeCategory.items.length >= MAX_QUESTIONS_PER_CATEGORY) {
                            Swal.fire(
                              "Limit reached",
                              `${activeCategory.displayName} can have at most ${MAX_QUESTIONS_PER_CATEGORY} questions.`,
                              "warning"
                            );
                            return;
                          }
                          openAddItemModal(activeCategoryIndex);
                        }}
                        aria-disabled={activeCategory.items.length >= MAX_QUESTIONS_PER_CATEGORY}
                        title={
                          activeCategory.items.length >= MAX_QUESTIONS_PER_CATEGORY
                            ? `Maximum ${MAX_QUESTIONS_PER_CATEGORY} questions per category`
                            : undefined
                        }
                        style={
                          activeCategory.items.length >= MAX_QUESTIONS_PER_CATEGORY
                            ? { opacity: 0.55, cursor: "not-allowed" }
                            : undefined
                        }
                      >
                        <i className="fa-solid fa-plus" /> Add Question
                      </button>
                      <button
                        className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                        onClick={() => singleCsvInputRef.current?.click()}
                        disabled={csvLoading}
                      >
                        <i className="fa-solid fa-file-arrow-up" /> Upload Selected CSV
                      </button>
                      <button
                        className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
                        onClick={() => bulkCsvInputRef.current?.click()}
                        disabled={csvLoading}
                      >
                        <i className="fa-solid fa-file-import" /> Upload All Categories CSV
                      </button>
                    </div>
                  </div>

                  <input
                    ref={singleCsvInputRef}
                    type="file"
                    accept=".csv"
                    hidden
                    onChange={(e) => handleCsvFileChange(e, "single")}
                  />
                  <input
                    ref={bulkCsvInputRef}
                    type="file"
                    accept=".csv"
                    hidden
                    onChange={(e) => handleCsvFileChange(e, "bulk")}
                  />
                </div>

                <div className="catmgr-import-strip">
                  <div className="catmgr-import-help">
                    {csvLoading ? (
                      "Processing CSV upload..."
                    ) : (
                      <ul className="catmgr-import-help-list">
                        <li>
                          <strong>Upload Selected CSV</strong> — replaces questions for the
                          active round only. Use a single <code>question</code> column (no{" "}
                          <code>category</code>). Same format for Round 1, 2, or 3.
                        </li>
                        <li>
                          <strong>Upload All Categories CSV</strong> — replaces questions
                          for every round in the file. Requires <code>category</code> and{" "}
                          <code>question</code> columns.
                        </li>
                        <li>
                          Each category: {MIN_QUESTIONS_PER_CATEGORY}–{MAX_QUESTIONS_PER_CATEGORY}{" "}
                          questions. Max {ITEM_TEXT_MAX_LEN} characters per question.
                        </li>
                        <li>Download the matching sample file below before you upload.</li>
                      </ul>
                    )}
                  </div>
                  <div className="catmgr-sample-links">
                    {SAMPLE_CSV_FILES.map((sample) => (
                      <a
                        key={sample.file}
                        className="catmgr-sample-link"
                        href={publicDownloadUrl(`/sample-csv/${sample.file}`)}
                        download
                      >
                        {sample.label}
                      </a>
                    ))}
                  </div>
                </div>

                {visibleItems.length === 0 ? (
                  <p className="catmgr-no-items">
                    No questions yet — click Add Question or upload a CSV file.
                  </p>
                ) : (
                  <div className="catmgr-table-wrap">
                    <table className="catmgr-table">
                      <thead>
                        <tr>
                          <th>No.</th>
                          <th>Question</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleItems.map((item) => {
                          const itemIndex = activeCategory.items.indexOf(item);
                          return (
                            <tr key={`${activeCategory.key}-${itemIndex}`}>
                              <td>{itemIndex + 1}</td>
                              <td className="catmgr-question-cell" title={item.name}>
                                {item.name}
                              </td>
                              <td>
                                <div className="catmgr-table-row-actions">
                                  <button
                                    className="catmgr-icon-btn catmgr-icon-btn-sm"
                                    title="Edit"
                                    onClick={() => openEditItemModal(activeCategoryIndex, itemIndex)}
                                  >
                                    <i className="fa-solid fa-pen" />
                                  </button>
                                  <button
                                    className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                                    title={
                                      activeCategory.items.length <= MIN_QUESTIONS_PER_CATEGORY
                                        ? `At least ${MIN_QUESTIONS_PER_CATEGORY} question required`
                                        : "Delete"
                                    }
                                    disabled={activeCategory.items.length <= MIN_QUESTIONS_PER_CATEGORY}
                                    onClick={() => handleDeleteItem(activeCategoryIndex, itemIndex)}
                                  >
                                    <i className="fa-solid fa-trash" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══ Add Category Modal ══════════════════════════════════ */}
      {addCatOpen && (
        <>
          <div className="catmgr-backdrop" onClick={closeAddCategoryModal} />
          <div className="catmgr-modal">
            <h3>Add Category</h3>
            <label className="catmgr-modal-label">Display name</label>
            <input
              autoFocus className="catmgr-modal-input" placeholder="e.g. Travel & Places"
              value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddCategory()}
           maxLength={CATEGORY_NAME_MAX_LEN} />
            {newCatName.trim() && (
              <p className="catmgr-modal-hint">Key: <code>{toSlugKey(newCatName)}</code></p>
            )}
            <label className="catmgr-modal-label" style={{ marginTop: 14 }}>
              Category image <span className="catmgr-optional">(optional)</span>
            </label>
            <div className="catmgr-upload-preview catmgr-upload-preview-landscape">
              {catImagePreview ? (
                <img src={catImagePreview} alt="preview" className="catmgr-upload-thumb" />
              ) : catImageFilename ? (
                <ItemImage src={S3 + catImageFilename} alt="current" className="catmgr-upload-thumb" />
              ) : (
                <div className="catmgr-upload-placeholder">
                  <i className="fa-solid fa-image" />
                  <span>Category image</span>
                </div>
              )}
            </div>
            <input
              ref={catFileRef}
              type="file"
              accept=".jpeg,.jpg,.png"
              className="catmgr-file-input"
              onChange={handleCategoryFileChange}
            />
            <p className="catmgr-modal-hint" style={{ marginTop: 4 }}>
              * JPEG / JPG / PNG only. Max 5 MB.
              <br />
              * Required size: {REQUIRED_CATEGORY_IMAGE_W}×{REQUIRED_CATEGORY_IMAGE_H} px.
            </p>
            <p className="catmgr-modal-hint" style={{ marginTop: 4 }}>
              * Maximum {CATEGORY_NAME_MAX_LEN} characters.
            </p>
            <p className="catmgr-modal-hint" style={{ marginTop: 4 }}>
              * Maximum {MAX_CATEGORY_COUNT} categories allowed.
            </p>
            <div className="catmgr-modal-actions">
              <button onClick={closeAddCategoryModal} disabled={catUploading}>Cancel</button>
              <button
                className="primary"
                onClick={handleAddCategory}
                disabled={catUploading}
              >
                {catUploading ? "Uploading…" : "Add"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══ Edit Category Modal ══════════════════════════════════ */}
      {editCatModal && (
        <>
          <div className="catmgr-backdrop" onClick={closeEditCategoryModal} />
          <div className="catmgr-modal">
            <h3>Edit Category</h3>
            <label className="catmgr-modal-label">Display name</label>
            <input
              autoFocus className="catmgr-modal-input" value={editCatName}
              onChange={(e) => setEditCatName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleEditCategory()}
              maxLength={CATEGORY_NAME_MAX_LEN}
            />
            {editCatName.trim() && (
              <p className="catmgr-modal-hint">Key: <code>{toSlugKey(editCatName)}</code></p>
            )}
            <label className="catmgr-modal-label" style={{ marginTop: 14 }}>
              Category image <span className="catmgr-optional">(optional)</span>
            </label>
            <div className="catmgr-upload-preview catmgr-upload-preview-landscape">
              {catImagePreview ? (
                <img src={catImagePreview} alt="preview" className="catmgr-upload-thumb" />
              ) : catImageFilename ? (
                <ItemImage src={S3 + catImageFilename} alt="current" className="catmgr-upload-thumb" />
              ) : (
                <div className="catmgr-upload-placeholder">
                  <i className="fa-solid fa-image" />
                  <span>Category image</span>
                </div>
              )}
            </div>
            <input
              ref={catFileRef}
              type="file"
              accept=".jpeg,.jpg,.png"
              className="catmgr-file-input"
              onChange={handleCategoryFileChange}
            />
            <p className="catmgr-modal-hint" style={{ marginTop: 4 }}>
              * JPEG / JPG / PNG only. Max 5 MB.
              <br />
              * Required size: {REQUIRED_CATEGORY_IMAGE_W}×{REQUIRED_CATEGORY_IMAGE_H} px.
            </p>
            <div className="catmgr-modal-actions">
              <button onClick={closeEditCategoryModal} disabled={catUploading}>Cancel</button>
              <button className="primary" onClick={handleEditCategory} disabled={catUploading}>
                {catUploading ? "Uploading…" : "Save"}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ══ Add / Edit Item Modal ════════════════════════════════ */}
      {itemModal && (
        <>
          <div className="catmgr-backdrop" onClick={closeItemModal} />
          <div className="catmgr-modal catmgr-modal-wide">
            <h3>
              {itemModal.mode === "add"
                ? `Add Question — ${categories[itemModal.catIndex]?.displayName}`
                : "Edit Question"}
            </h3>
            <label className="catmgr-modal-label" style={{ marginTop: 12 }}>
              Question text
            </label>
            <textarea
              autoFocus
              className="catmgr-modal-input catmgr-modal-textarea"
              placeholder="Enter question"
              value={itemName}
              onChange={(e) => handleItemNameChange(e.target.value)}
              maxLength={ITEM_TEXT_MAX_LEN}
              rows={5}
            />
            <p className="catmgr-modal-hint" style={{ marginTop: 4 }}>
              * Maximum {ITEM_TEXT_MAX_LEN} characters.
              <br />
              * Each category: {MIN_QUESTIONS_PER_CATEGORY}–{MAX_QUESTIONS_PER_CATEGORY} questions.
            </p>

            <div className="catmgr-modal-actions">
              <button onClick={closeItemModal}>Cancel</button>
              <button className="primary" onClick={handleSaveItem}>
                {itemModal.mode === "add" ? "Add" : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CustomizeSpotTheme;
